import { useDebounce } from "@/common/helpers/useDebounce";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { InfiniteScrollTableColumnDefinition } from "@/common/types/infiniteScrollTable";
import { TableSettingsCache } from "@/components/atoms/customTable";
import { useEffect, useRef, useState } from "react";
import { useColumnManagement } from "../columnManagement";
import { SortState } from "@/components/organisms/history/types";

export interface InfiniteScrollingTableFeatures {
	columns: InfiniteScrollTableColumnDefinition[];
	removeColumn: (index: number) => void;
	addDeletedColumn: () => void;
	resetToDefaultTableSettings: () => void;
	toggleSort: (column: string) => void;
	sortState: SortState;
}

export const useInfiniteScrollingTableColumns = (
	columnsDefinition: InfiniteScrollTableColumnDefinition[],
	tableCacheKey: CacheKey,
	defaultSort: SortState,
	tableSortCacheKey: CacheKey,
	excludedColumnIds: string[] = []
): InfiniteScrollingTableFeatures => {
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const cacheDataRef = useRef<TableSettingsCache>();

	const tableSettingsCache = (appStoreCache[tableCacheKey] ||
		{}) as TableSettingsCache;

	const { columns, removeColumn, resetDefaultColumns, addDeletedColumn } =
		useColumnManagement<InfiniteScrollTableColumnDefinition>(
			columnsDefinition,
			tableSettingsCache,
			excludedColumnIds
		);

	// Joined so the effect below depends on the contents, not on the array identity a caller hands us.
	const excludedColumnIdsKey = excludedColumnIds.join(",");

	useEffect(() => {
		return () => {
			appStoreActions.setCache(tableCacheKey, cacheDataRef.current);
		};
	}, []);

	const [sortState, setSortState] = useState<SortState>(
		appStoreCache[tableSortCacheKey]
			? appStoreCache[tableSortCacheKey]
			: defaultSort
	);

	useEffect(() => {
		const currentColumnIds = columns.map(({ columnId }) => columnId);
		const allColumnIds = columnsDefinition.map((x) => x.columnId);
		// Excluded columns are absent because the tenant configuration has nothing to show in them, not
		// because the agent hid them. Persisting them as hidden would keep them gone after the tenant
		// gains its first tag. Exclusion-set changes themselves are applied inside useColumnManagement
		// during render, so `columns` is always settled by the time this effect observes it.
		const missingColumnIds = allColumnIds.filter(
			(x) =>
				!currentColumnIds.includes(x) &&
				!excludedColumnIds.includes(x.toString())
		);

		const cacheModel = {
			hiddenColumnIds: missingColumnIds,
		} as TableSettingsCache;

		cacheDataRef.current = cacheModel;

		saveCacheModel();
	}, [columns, excludedColumnIdsKey]);

	const [saveCacheModel] = useDebounce(() => {
		appStoreActions.setCache(tableCacheKey, cacheDataRef.current);
	}, 500);

	const resetToDefaultTableSettings = () => {
		resetDefaultColumns();
		setSortState(defaultSort);
	};

	const toggleSort = (column: string) => {
		setSortState((currentState) => {
			const newSortState = { ...currentState };

			if (newSortState.column === column) {
				newSortState.direction =
					newSortState.direction === "asc" ? "desc" : "asc";
			} else {
				newSortState.column = column;
				newSortState.direction = "asc";
			}

			return newSortState;
		});
	};

	return {
		columns,
		removeColumn,
		addDeletedColumn,
		resetToDefaultTableSettings,
		toggleSort,
		sortState,
	};
};
